import React, {useCallback, useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {connect, ConnectedProps, useDispatch} from "react-redux";
import {IDateRangeGState, ILoadingComponents} from "../../../../interfaces/datesGlobalState.interface";
import {RootState} from "../../../../interfaces/root-state.interface";
import {publisherTagsBaseUrl} from "../../../../config/generals";
import TagsTable, {IRowConnectedTag} from "./ConnectedTagsTable/publisherConnectedTag"
import DiscrepancyMetrics from "../../common/DiscrepancyMetrics/index"
import {AlertTypes, apiClient, Grid, setAlert, Paper, Typography, Skeleton, useTheme, Toc as TocIcon, GpsFixed as GpsFixedIcon, MuiIcons} from "@leap4/frontend";
import DiscrepancyChart, {ICustomDataSet} from "../../common/DiscrepancyChart/index";
import ReportHeader from "../../common/ReportHeader/ReportHeader";
import {urlService} from "../../../routing/urlsService";
import {setIsLoading} from "../../../../store/actions/dateRange.actions";
import {useRefreshInterval} from "../../../../store/hooks";
import {IApiResPublisherTagPage} from "./interfaces/IPublisherTagPage";
import { IGraphTimeBucket } from "./interfaces/IGraphTimeBucket";
import {IGraphTimeBucket as frGraphTimeBuckets} from "@leap4/entities-discrepancy-common"
import moment from "moment";

const { Tag, Person4: Person4Icon } = MuiIcons;


const mapStateToProps = (state: RootState) => ({
    dates: state.dates,
    selectedOrganization:state.auth.selectedOrganizationId
});

const mapActions = {
    setIsLoading
}

const connector = connect(mapStateToProps, mapActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

export interface IProps extends PropsFromRedux {
    dates: IDateRangeGState
    setIsLoading: (isLoading: Partial<ILoadingComponents>) => void
}

export interface IConnectedTagGraphDataSet {
    connectedTag: IRowConnectedTag,
    dataSet: ICustomDataSet
}

function mapGraphBuckets(buckets: IGraphTimeBucket[]): frGraphTimeBuckets[]{
    if(!buckets){
        return []
    }
    return buckets.map(bucket => {
        return {
            timestamp: bucket.timestamp,
            connectedTags:bucket. connectedTags,
            publisher: bucket.publisher,
            discrepancy: {
                revenue: {
                    value: bucket.discrepancy.revenueDiscValue,
                    percent: bucket.discrepancy.revenueDiscPercent
                },
                impressions: {
                    value: bucket.discrepancy.impressionsDiscValue,
                    percent: bucket.discrepancy.impressionsDiscPercent
                }
            }
        }
    })
}

const PublisherTagPage: React.FC<IProps> = ({
                                                dates: {startDate, endDate, isLoading, isLoadingAny},
                                                setIsLoading,
                                                selectedOrganization
                                            }): JSX.Element => {
    const [data, setData] = useState<IApiResPublisherTagPage>(null);
    const [graphData, setGraphData] = useState<IGraphTimeBucket[]>(null)
    const theme = useTheme()
    useRefreshInterval(()=>{
        if (startDate === endDate && startDate === moment().format("YYYY-MM-DD")) {
            fetchData()
            fetchGraphData()
        }
    })
    const params = useParams<{ id: string }>()
    const dispatch = useDispatch();

    const fetchData = useCallback(() => {
        (async () => {
            try {
                setIsLoading({isLoadingGeneral: true});
                const baseUrl = `${publisherTagsBaseUrl}/tags/${params.id}`;
                const apiResponse = await apiClient.post(baseUrl, {
                    startDate,
                    endDate
                })
                const apiData: IApiResPublisherTagPage = apiResponse.data
                setData(apiData)

            } catch (err) {
                setAlert({msg: "Could not fetch data", type: AlertTypes.FAILURE, d: dispatch})
                setData(null)

            } finally {
                setIsLoading({isLoadingGeneral: false});
            }
        })()

    }, [startDate, endDate, params.id, dispatch, setIsLoading]);

    const fetchGraphData = useCallback(() => {
        (async () => {
            try {
                setIsLoading({isLoadingGraph: true});
                const baseUrl = `${publisherTagsBaseUrl}/tags/${params.id}/graph`
                const graphResponse = await apiClient.post(baseUrl, {startDate, endDate})
                const graphData = graphResponse.data
                setGraphData(graphData)
            } catch (err) {
                setAlert({msg: "Couldn't fetch Graph Data", type: AlertTypes.FAILURE, d: dispatch})
                setGraphData(null)
            } finally {
                setIsLoading({isLoadingGraph: false});
            }
        })()
    }, [startDate, endDate, params.id, dispatch, setIsLoading]);

    useEffect(() => { fetchData(); }, [fetchData, selectedOrganization]);
    useEffect(() => { fetchGraphData(); }, [fetchGraphData, selectedOrganization]);


    const ReportHeaderWithProps = () => <ReportHeader breadcrumbsIcon={<Tag/>} breadcrumbs={[
        {text: "Publisher Data Points", link: `${urlService.publisherDataPoints(true)}`, icon: <TocIcon />},
        {
            text: data?.publisher?.dataPoint?.name || "Loading...",
            link: urlService.publisherDataPoint(data?.publisher?.dataPoint?._id || ":id", true),
            icon: <Person4Icon />
        },
        {
            text: data?.publisher?.tag?.name || "loading...",
            icon: <GpsFixedIcon/>
        }
    ]}/>

    if (!isLoadingAny && !data) return (
        <>
            <ReportHeaderWithProps/>
            <Typography variant={"overline"}> Error with getting the data...</Typography>
        </>)
    return (
        <>
            <ReportHeaderWithProps/>
            <Grid container justifyContent={{sm: 'center', md: "space-between"}}
                  style={{
                      wordBreak: 'break-all',
                      filter: theme.palette.mode === 'dark'? 'drop-shadow(0px 0px 2px lightgray' : 'drop-shadow(0px 0px 5px lightgray',
                      margin:'0 auto',
                  }} direction={"row"}
                  spacing={2}>
                <>
                    <Grid item sm={12}>
                        {isLoading.isLoadingGeneral || !data ?
                            <Skeleton sx={{borderRadius: "16px"}} variant={"rounded"} height={"150px"} width={"100%"} animation={"wave"}/> :
                            <Paper elevation={5}
                                   style={{
                                       padding: "10px 0",
                                       height: '100%',
                                       margin: '0 10px',
                                       display: 'flex',
                                       alignItems: 'center',
                                       boxShadow: 'none',
                                       borderRadius: "16px"
                                   }}>
                                <Grid container height={"100%"} justifyContent={"space-around"}>
                                    <Grid item height={"100%"} width={"100%"}>
                                        <DiscrepancyMetrics
                                            adServersTotals={data.connectedTags?.totals}
                                            publisherTotals={data.publisher.totals}
                                            discrepancy={{
                                                revenue: {
                                                    value: data.discrepancy.revenueDiscValue,
                                                    percent: data.discrepancy.revenueDiscPercent
                                                },
                                                impressions: {
                                                    value: data.discrepancy.impressionsDiscValue,
                                                    percent: data.discrepancy.impressionsDiscPercent
                                                }
                                            }}
                                            subtitle={`${data.publisher.dataPoint.name}, ${data.publisher.dataPoint.adServer.name}`}
                                            title={data.publisher.tag.name.toUpperCase()}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>}
                    </Grid>
                    <Grid item sx={{height: '400px', margin: '0 auto', width: {xs: '95%', sm: '96%', md: '99%'}}}>
                        <DiscrepancyChart {...{
                            graphData: mapGraphBuckets(graphData),
                            startDate,
                            endDate,
                            isLoading: isLoading.isLoadingGraph
                        }} />
                    </Grid>
                    <Grid item xs={12} style={{margin: '0 10px'}}>
                        {!isLoading.isLoadingGeneral && data ?
                            <TagsTable items={{tags: data.connectedTags?.tags || [],publisherTags: data.connectedTags?.publisherTags}} loading={isLoading.isLoadingGeneral}
                                       refreshFunc={fetchData}
                                      /> :
                            <Skeleton sx={{borderRadius: "16px"}} variant={"rounded"} animation={"wave"} height={"300px"}/>}
                    </Grid>
                </>
            </Grid>
        </>)

}


export default connector(PublisherTagPage);